import { Dispatch } from 'redux';
import HubspotController from '../controllers/HubspotController';
import commonHandler from '../errorHandlers/common-handler';
import {
  HubspotAccessToken,
  HubspotOauthToken,
} from '../model/ConnectionModel';
import { OauthUrlResponse } from '../model/KlaviyoModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class HubspotCRUDActions extends CrudActionContainer {
  retrieveOauthUrlAction: string;
  retrieveLists: string;
  revokeAction: string;
  retrieveAccounts: string;
  resetStore: string;
  retrieveRevenue: string;
  retrievedAccessToken: string;

  constructor() {
    super('HUBSPOT');
    this.retrieveLists = this.makeActionKey('RETRIEVED_LISTS');
    this.retrieveOauthUrlAction = this.makeActionKey('RETRIEVED_OAUTH_URL');
    this.retrieveAccounts = this.makeActionKey('RETRIEVED_ACCOUNTS');
    this.revokeAction = this.makeActionKey('REVOKE');
    this.resetStore = this.makeActionKey('RESET_STORE');
    this.retrieveRevenue = this.makeActionKey('RETRIEVED_REVENUE');
    this.retrievedAccessToken = this.makeActionKey('RETRIVED_ACCESS_TOKEN');
  }

  getOauthUrl(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveOauthUrlAction));
      try {
        const response: OauthUrlResponse = await HubspotController.getOauthUrl(
          facebookPageId,
        );
        dispatch({
          type: this.retrieveOauthUrlAction,
        });

        return response.link;
      } catch (error) {
        commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getAccounts(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAccounts));
      try {
        const response = await HubspotController.getAccounts(facebookPageId);
        dispatch({
          type: this.retrieveAccounts,
          accounts: response,
        });
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  revoke(tokenId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.revokeAction));

      try {
        await HubspotController.revokeToken(tokenId);
        dispatch({ type: this.revokeAction, id: tokenId });
        return true;
      } catch (error) {
        commonHandler(dispatch)(error);
        return false;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  revokeAccessToken(tokenId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.revokeAction));

      try {
        await HubspotController.revokeAccessToken(tokenId);
        dispatch({ type: this.revokeAction, id: tokenId });
        return true;
      } catch (error) {
        commonHandler(dispatch)(error);
        return false;
      }
    };
  }

  getLists(
    facebookPageId: number,
    account: HubspotAccessToken | HubspotOauthToken,
    force: boolean = false,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveLists));
      try {
        const response = await HubspotController.getLists(
          facebookPageId,
          account,
          force,
        );
        dispatch({
          type: this.retrieveLists,
          lists: response,
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
        return false;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  updateToken(facebookPageId: number, id: number, privateKey: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));

        const res = await HubspotController.updateAccessToken(
          facebookPageId,
          id,
          privateKey,
        );

        dispatch({ type: this.retrievedAccessToken, account: res });
        return res;
      } catch (error) {
        commonHandler(dispatch)(error);
        return false;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  invalidateStore() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.resetStore });
    };
  }
}

const hubspotCRUDActions = new HubspotCRUDActions();

export default hubspotCRUDActions;

export const useHubspotCRUDActions = getActionHookBuilder(hubspotCRUDActions, [
  'getOauthUrl',
  'getAccounts',
  'revoke',
  'getLists',
  'invalidateStore',
  'revokeAccessToken',
  'updateToken',
]);
